import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { SelectDropdown, TextAreaInput } from "../../reusables/FormElements";
import ParametersTable from "../../sub_components/ParametersTable";

import { IParametersData, IProgramFunctionData, IProgramFunctionFormProps, ProgramFunctionField } from "../../../interfaces/forms";
import { ISelectDropdownOption } from "../../../interfaces/form_elements";

import useSaveProgramFunction from "../../../hooks/program_functions/useSaveProgramFunction";
import useDeleteProgramFunction from "../../../hooks/program_functions/useDeleteProgramFunction";
import useGetMetaFunctions from "../../../hooks/meta_functions/useGetMetaFunctions";
import useGetNocCodes from "../../../hooks/jobs/useGetNocCodes";

import { closeModal, openModal } from "../../../store/slices/modal";

const ProgramFunctionForm = (props: IProgramFunctionFormProps) => {
    const { programFunctionData, selected, setSelected, setExpanded } = props;

    const dispatch = useDispatch();

    const requiredFields: ProgramFunctionField[] = ["name"];
    const selectFields: ProgramFunctionField[] = ["name"];

    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<IProgramFunctionData>({
        id: null,
        component_id: null,
        name: { value: { value: "", label: "" }, error: "" },
        description: { value: "", error: "" },
    });
    const [parameters, setParameters] = useState<IParametersData[]>([]);

    const { mutate: saveProgramFunction } = useSaveProgramFunction();
    const { mutate: deleteProgramFunction } = useDeleteProgramFunction();
    const { data: metaFunctions } = useGetMetaFunctions();
    const { data: nocCodes } = useGetNocCodes();

    useEffect(() => {
        if (programFunctionData) {
            const metaFunction = metaFunctions?.find(meta => meta.name === programFunctionData.name);

            setFormData({
                id: programFunctionData.id > 0 ? programFunctionData.id : null,
                component_id: programFunctionData.component_id,
                name: { value: { value: metaFunction?.name ?? '', label: metaFunction?.name ?? '' }, error: '' },
                description: { value: programFunctionData.description ?? '', error: '' },
            });

            const newParameters: IParametersData[] = [];
            for (const param of programFunctionData.parameters) {
                newParameters.push({
                    id: param.id,
                    name: param.name,
                    value: param.value,
                });
            };
            setParameters(newParameters);
        } else {
            setFormData({
                id: null,
                component_id: null,
                name: { value: { value: "", label: "" }, error: "" },
                description: { value: "", error: "" },
            });

            setParameters([]);
        };

        // eslint-disable-next-line
    }, [programFunctionData, metaFunctions]);

    const onChangeText = (name: ProgramFunctionField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onFunctionChange = (option: ISelectDropdownOption) => {
        if (option.value === formData.name.value.value) return;

        if (!formData.name.value.value?.length) {
            onChangeSelect("name", option);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Do you want to change the function?",
                content: "This will delete all its parameters. This action cannot be undone.",
                actionTxt: "Confirm",
                action: () => {
                    onChangeSelect("name", option);
                    setParameters([]);

                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }));
    };

    const onChangeSelect = (name: ProgramFunctionField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.name.value.value === (programFunctionData?.name ?? '') &&
            formData.description.value === (programFunctionData?.description ?? '');

        let isParametersSame = true;
        if (parameters.length !== programFunctionData?.parameters.length) {
            isParametersSame = false;
        } else {
            for (const parameter of parameters) {
                const existingParam = programFunctionData?.parameters.find(p => p.name === parameter.name);
                if (!existingParam || existingParam.value !== parameter.value) {
                    isParametersSame = false;
                    break;
                };
            };
        };

        return emptyFields || (isDataSame && isParametersSame) || isProcessing || !formData.component_id;
    };

    const onSave = () => {
        if (isDisabled() || !selected) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id" || key === "component_id") return;
                newData[key as ProgramFunctionField].error = "";
            });
            return newData;
        });

        // check if parameters are of selected function
        if (parameters.length) {
            const metaFunction = metaFunctions?.find(meta => meta.name === formData.name.value.value);
            const metaParamNames = metaFunction?.parameters.map(param => param.name) ?? [];

            const invalidParams = parameters.filter(param => !metaParamNames.includes(param.name));
            if (invalidParams.length) {
                return dispatch(openModal({
                    name: 'MESSAGE_MODAL',
                    data: {
                        view: 'CONFIRMATION',
                        closeBtn: true,
                        title: "Invalid parameters",
                        content: "Some parameters are not valid for the selected function. Please refresh the page and try again.",
                        actionTxt: "Refresh",
                        action: () => window.location.reload(),
                        cancelTxt: "Close",
                    }
                }));
            };
        };

        setIsProcessing(true);

        saveProgramFunction({
            payload: {
                id: formData.id,
                component_id: formData.component_id as number,
                name: formData.name.value.value,
                description: formData.description.value,
                parameters,
            },
            setIsProcessing,
            selected,
            setSelected,
        });
    };

    const onDelete = () => {
        if (!selected) return;

        if (!formData.id) {
            setSelected(null);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Confirm delete function?",
                content: "This will delete the function and all its parameters. This action cannot be undone.",
                actionTxt: "Delete",
                action: () => {
                    setIsProcessing(true);

                    deleteProgramFunction({
                        id: formData.id as number,
                        setIsProcessing,
                        setSelected,
                        setExpanded,
                    });

                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }));
    };

    return (
        <div className="immployer_admin__common_form">
            <div className="immployer_admin__common_form__inputs">
                <SelectDropdown
                    name="name"
                    label="Select program function"
                    required={true}
                    placeholder="-- select option --"
                    value={formData.name.value}
                    onChange={(option: ISelectDropdownOption) => onFunctionChange(option)}
                    errorMsg={formData.name.error}
                    options={!metaFunctions?.length ? [] : metaFunctions?.map(metaFunction => ({ value: metaFunction.name, label: metaFunction.name }))}
                    read_only={!metaFunctions?.length}
                />
                <TextAreaInput
                    name="description"
                    max_length={200}
                    label="Function Description"
                    errorMsg={formData.description.error}
                    placeholder="Enter function description"
                    onChange={(value: string) => onChangeText("description", value)}
                    value={formData.description.value}
                />
                <br />
                <h4 className="color_dark">Parameters:</h4>
                <ParametersTable
                    parameters={parameters}
                    setParameters={setParameters}
                    metaParameters={metaFunctions?.find(meta => meta.name === formData.name.value.value)?.parameters ?? []}
                    nocCodes={nocCodes ?? []}
                />
            </div>
            <div className="immployer_admin__common_form__actions">
                <button
                    className={`immployer_admin__btn immployer_admin__btn__secondary ${isProcessing ? "immployer_admin__btn__secondary__disabled" : ""}`}
                    onClick={() => isProcessing ? null : onDelete()}
                >
                    {!formData.id ? 'Discard' : 'Delete'}
                </button>
                <button
                    className={`immployer_admin__btn immployer_admin__btn__primary ${isDisabled() ? "immployer_admin__btn__primary__disabled" : ""}`}
                    onClick={onSave}
                >
                    {
                        isProcessing
                            ? <i className='immployer_admin__btn_loader' />
                            : 'Save'
                    }
                </button>
            </div>
        </div>
    );
};

export default ProgramFunctionForm;